if (!customElements.get('product-selector')) {
  class ProductSelector extends HTMLElement {
    abortController = undefined;
    pendingRequestUrl = null;
    preProcessHtmlCallbacks = [];
    postProcessHtmlCallbacks = [];

    constructor() {
      super();
      this.contactModal = document.querySelector(
        `#Modal-Product-Contact-${this.getAttribute(
          'data-product-id'
        )}-${this.getAttribute('data-section-id')}`
      );
      this.cartDrawer = document.querySelector('cart-drawer');
      this.mainProduct = this.closest('main-product');
      this.enableScroll = this.hasAttribute('data-scroll-to-variant');
      this.variants = JSON.parse(
        this.querySelector('[type="application/json"]').textContent
      );
      this.addEventListener('change', this.onVariantChange);
    }

    connectedCallback() {
      this.initializeProductSwapUtility();

      this.recipientForm =
        this.mainProduct?.querySelector('form-recipient');

      this.installmentsForms = [
        document.querySelector(
          this.getAttribute('data-installments-form-id-title')
        ),
        document.querySelector(
          this.getAttribute('data-installments-form-id-price')
        )
      ];

      this.installmentsForm = document.querySelector(
        this.getAttribute('data-installments-form-id-title')
      );

      this.customFields = document.querySelectorAll(
        `[custom-field="${this.dataset.formId}"]`
      );

      this.attachEventListeners();
      this.updateURL();
      this.handleErrorMessage();
      this.productBarUpdateOptions();
      this.filterDropdownOptions();
    }

    onQuantityChange(event) {
      const productBarQuantity = this.productBar?.querySelector(
        '[name="quantity"]'
      );

      if (productBarQuantity) {
        productBarQuantity.value = event.target.value;
      }
    }

    cartSubmitHandler() {
      this.submitButton.classList.add('disabled');
      const sectionsToRender = [
        {
          id: '#cart-counter',
          section: 'cart-counter',
          selector: '#shopify-section-cart-counter'
        },
        {
          id: '#main-cart',
          section: 'main-cart',
          selector: '#main-cart'
        }
      ];

      addToCart(
        this.form,
        sectionsToRender.map(section => section.section)
      )
        .then(response => {
          if (response.status) {
            this.handleErrorMessage(response.description);
            return;
          }

          this.handleErrorMessage();

          if (this.isQuickviewSelector) {
            const quickviewModal = this.closest('modal-dialog');
            quickviewModal.hide();
          }

          if (window.location.pathname !== routes.cart_url) {
            window.location.href = routes.cart_url;
            return;
          }

          if (!response?.sections['main-cart']) {
            location.reload();
          }
          sectionsToRender.map(section => {
            const sectionToGet = section.section;
            if (!response?.sections[sectionToGet]) return;

            const source = new DOMParser().parseFromString(
              response.sections[sectionToGet],
              'text/html'
            );

            if (!source) return;

            const target = source.querySelector(section.selector);
            const destination = document.querySelector(section.id);

            if (!target || !destination) return;

            destination.innerHTML = target.innerHTML;
          });

          window.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
        })
        .catch(error => {
          console.error(error);
        })
        .finally(() => {
          this.submitButton.classList.remove('disabled');
        });
    }

    defaultSubmitHandler() {
      this.submitButton.classList.add('disabled');

      addToCart(
        this.form,
        this.cartDrawer
          .getSectionsToRender()
          .map(section => section.section)
      )
        .then(response => {
          if (response.status) {
            this.recipientForm?.displayErrorMessage(response.errors);
            this.handleErrorMessage(response.description);
            return;
          }

          this.handleErrorMessage();

          if (this.isQuickviewSelector) {
            const quickviewModal = this.closest('modal-dialog');
            quickviewModal.hide();
          }
          this.cartDrawer.renderContents(response);
        })
        .catch(error => {
          console.error(error);
        })
        .finally(() => {
          this.submitButton.classList.remove('disabled');
        });
    }

    onSubmitHandler(event) {
      event.preventDefault();

      if (!this.areCustomFieldsValid()) {
        return;
      }

      if (
        window.location.pathname == routes.cart_url ||
        !this.cartDrawer
      )
        return this.cartSubmitHandler();
      if (this.cartDrawer) return this.defaultSubmitHandler();
    }

    areCustomFieldsValid() {
      this.customFields.forEach(fieldWrapper => {
        this.handleCustomFieldError(
          fieldWrapper.querySelector('.js-field-wrapper')
        );
      });

      if (!this.customFields.length) {
        return true;
      }

      return [...this.customFields].every(fieldWrapper => {
        const field = fieldWrapper.querySelector('input, textarea');

        return !field || field.validity.valid;
      });
    }

    handleCustomFieldError(fieldWrapper) {
      const input = fieldWrapper?.querySelector('input, textarea');

      if (!input) return;
      input.setAttribute('aria-invalid', !input.validity.valid);
      const field = input.closest('.js-field-wrapper');

      if (!input.validity.valid) {
        input.addEventListener(
          'change',
          this.handleCustomFieldError.bind(this, fieldWrapper),
          { once: true }
        );
      }

      if (!field) return;
      field.classList.toggle('has-error', !input.validity.valid);

      const errorMessageSpan = field.querySelector(
        '.js-error-message'
      );

      if (!errorMessageSpan) return;

      errorMessageSpan.parentElement.classList.toggle(
        'hidden',
        input.validity.valid
      );
      errorMessageSpan.parentElement.classList.toggle(
        'has-error',
        !input.validity.valid
      );

      errorMessageSpan.innerHTML =
        input.type == 'email'
          ? window.validationStrings.invalidEmail
          : input.validationMessage;
    }

    handleErrorMessage(errorMessage = false) {
      const errorWrapper = this.querySelector('[data-error-wrapper]');
      if (!errorWrapper) return;
      errorWrapper.classList.toggle('hidden', !errorMessage);

      if (typeof errorMessage === 'object') return;

      errorWrapper.textContent = errorMessage || '';
    }

    onVariantChange(
      event,
      selectedOptions = this.selectedOptionValues
    ) {
      if (
        event.target.type === 'number' ||
        event.target.name === 'id'
      ) {
        return;
      }

      this.filterDropdownOptions();

      if (!this.isEveryOptionSelected()) return;

      const productUrl =
        event.target.dataset.productUrl ||
        this.pendingRequestUrl ||
        event.target.selectedOptions?.[0].dataset.productUrl ||
        this.dataset.url;

      this.pendingRequestUrl = productUrl;
      const shouldSwapProduct = this.dataset.url !== productUrl;

      this.renderProductInfo({
        requestUrl: this.buildRequestUrlWithParams(
          productUrl,
          selectedOptions.map(value => value.id),
          shouldSwapProduct && !this.isQuickviewSelector
        ),
        targetId: !event.target.closest('product-bar')
          ? event.target.id
          : undefined,
        callback: shouldSwapProduct
          ? this.handleSwapProduct(this.isQuickviewSelector)
          : this.handleUpdateProductInfo(event)
      });
    }

    /**
     * Update product bar options.
     *
     * @returns {void}
     */
    productBarUpdateOptions() {
      if (!this.productBar || this.isQuickviewSelector) return;

      Array.from(this.productBar.querySelectorAll('[data-name]')).map(
        selector => {
          selector.value = this.selectedOptionValues.find(
            option => option.name === selector.dataset.name
          ).value;
        }
      );
    }

    updateMedia(data) {
      if (!this.selectedVariant) return;

      if (this.isQuickviewSelector) {
        const quickview = this.closest('product-quickview');
        const images = quickview.querySelectorAll('[data-image]');
        const targetImage = Array.from(images).find(image => {
          return (
            image.dataset.variantIds?.includes(
              String(this.selectedVariant?.id)
            ) || image.dataset[`image${data.name}`] === data.value
          );
        });

        if (!targetImage) return;

        images.forEach(image =>
          image.classList.toggle('hidden', targetImage !== image)
        );

        return;
      }

      const productMedia =
        this.closest('main-product')?.querySelector('product-media');
      if (!productMedia) return;
      if (productMedia.hasAttribute('data-gallery-filter')) {
        productMedia.filterMedia(data);
        return;
      }
      this.enableScroll &&
        productMedia.setActiveMedia(
          this.selectedVariant?.featured_media?.id || ''
        );
    }

    updateURL(sellingPlanId) {
      const parent =
        this.closest('product-quickview') ||
        this.closest('main-product');

      if (this.selectedVariant) {
        parent
          .querySelector('[data-go-to-product]')
          ?.setAttribute(
            'href',
            `${
              this.isQuickviewSelector
                ? parent.dataset.url
                : this.dataset.url
            }/?variant=${this.selectedVariant.id}`
          );
      }

      if (this.isQuickviewSelector) return;
      if (!window.location.pathname.includes('/products')) return;

      const params = new URLSearchParams(window.location.search);
      if (this.selectedVariant) {
        params[params.has('variant') ? 'set' : 'append'](
          'variant',
          this.selectedVariant.id
        );
      } else {
        params.delete('variant');
      }

      if (sellingPlanId) {
        params[params.has('selling_plan') ? 'set' : 'append'](
          'selling_plan',
          sellingPlanId
        );
      } else {
        params.delete('selling_plan');
      }
      window.history.replaceState(
        {},
        '',
        `${this.dataset.url}${
          params.toString() ? `?${params.toString()}` : ''
        }`
      );
    }

    updateVariantInput() {
      const inputs = [this.form.querySelector('[name="id"]')];

      this.installmentsForms.forEach(form => {
        if (!form) return;
        const input = form?.querySelector('[name="id"]');
        input && inputs.push(input);
      });

      inputs.forEach(input => {
        if (!input) return;
        input.value = this.selectedVariant?.id ?? '';
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    setButtonsUnavailable() {
      if (!this.submitButton) return;

      [
        this.submitButton,
        this.productBar?.querySelector('[type="submit"]')
      ].forEach(submitButton => {
        if (!submitButton) return;

        submitButton.querySelector('span').textContent =
          window.variantStrings.unavailable;
      });
    }

    /**
     * Update live regions.
     * @param {HTMLElement} html
     */
    updateLiveRegions(html) {
      const regions = [
        '[data-selected-variant]',
        this.dataset.formId,
        `[name="add"][form="product-form-${this.dataset.sectionId}"]`,
        `#Product-Badges-${this.dataset.sectionId}`,
        `#Product-Price-${this.dataset.sectionId}-title`,
        `#Product-Price-${this.dataset.sectionId}-price`,
        `#Product-Stock-Notification-${this.dataset.sectionId}`,
        `#Product-Pickup-availability-${this.dataset.sectionId}`,
        `#Product-Purchase-Options-${this.dataset.sectionId}`,
        `#Product-SKU-${this.dataset.sectionId}`,
        `#ProductSelector-${this.dataset.sectionId}`
      ];
      if (!this.isQuickviewSelector) {
        regions.push('product-bar');
      }
      const sourceHTML =
        html
          .querySelector('template#quickview')
          ?.content.firstElementChild.cloneNode(true) || html;

      regions.forEach(region => {
        const destination = document.querySelector(region);
        const source = sourceHTML.querySelector(region);

        if (!destination || !source) return;

        HTMLUpdateUtility.viewTransition(
          destination,
          source,
          this.preProcessHtmlCallbacks,
          this.postProcessHtmlCallbacks
        );
      });
    }

    updateContactModal() {
      if (!this.contactModal) return;
      const variantTitle = this.contactModal.querySelector(
        '[data-contact-modal-variant]'
      );
      const variantTitleInput = this.contactModal.querySelector(
        '[name="contact[Variant]"]'
      );

      variantTitle.textContent = this.selectedVariant.title;
      variantTitleInput.value = this.selectedVariant.title;
    }

    /**
     * Attach event listeners.
     */
    attachEventListeners() {
      this.form.addEventListener(
        'submit',
        this.onSubmitHandler.bind(this)
      );

      setTimeout(() => {
        if (this.quantity) {
          this.quantity.addEventListener(
            'change',
            this.onQuantityChange.bind(this)
          );
        }
      });
    }

    /**
     * Initialize product swap utility.
     */
    initializeProductSwapUtility() {
      this.postProcessHtmlCallbacks.push(newNode => {
        window?.Shopify?.PaymentButton?.init();
      });
    }

    /**
     * Render product info.
     *
     * @param {String} requestUrl
     * @param {String} targetId
     * @param {Function} callback
     */
    renderProductInfo({ requestUrl, targetId, callback }) {
      this.abortController?.abort();
      this.abortController = new AbortController();

      fetch(requestUrl, { signal: this.abortController.signal })
        .then(response => response.text())
        .then(responseText => {
          this.pendingRequestUrl = null;
          const html = new DOMParser().parseFromString(
            responseText,
            'text/html'
          );

          callback(html);
        })
        .then(() => {
          // set focus to last clicked option value
          // document.querySelector(`#${targetId}`)?.focus();
        })
        .catch(error => {
          if (error.name === 'AbortError') {
            console.log('Fetch aborted by user');
          } else {
            console.error(error);
          }
        });
    }

    /**
     * Build request URL with params.
     *
     * @param {String} url
     * @param {Array} optionValues
     * @param {Boolean} shouldFetchFullPage
     * @returns {String}
     */
    buildRequestUrlWithParams(
      url,
      optionValues,
      shouldFetchFullPage
    ) {
      const params = [];
      const parent =
        this.closest('product-quickview') ||
        this.closest('main-product');
      const sectionId = parent?.dataset.section;

      sectionId &&
        !shouldFetchFullPage &&
        params.push(`section_id=${sectionId}`);

      if (optionValues.length) {
        params.push(`option_values=${optionValues.join(',')}`);
      }

      return `${url}?${params.join('&')}`;
    }

    /**
     * Hide variant specific elements.
     */
    setUnavailable() {
      const selectors = [
        `#Product-Price-${this.dataset.sectionId}-price`,
        `#Product-Price-${this.dataset.sectionId}-title`,
        `#Product-SKU-${this.dataset.sectionId}`,
        `#Product-Stock-Notification-${this.dataset.sectionId}`,
        `#Product-Pickup-availability-${this.dataset.sectionId}`,
        `#Product-Purchase-Options-${this.dataset.sectionId}`
      ];
      document
        .querySelectorAll(selectors)
        .forEach(({ classList }) =>
          classList.add('visibility-hidden')
        );

      this.setButtonsUnavailable();
    }

    /**
     * Handle swap product.
     *
     * @param {Boolean} isQuickview
     * @returns {Function}
     */
    handleSwapProduct(isQuickview) {
      return html => {
        if (isQuickview) {
          const currentQuickView =
            this.closest('product-quickview') ||
            this.closest('main-product');

          const newQuickView = html
            .querySelector('#quickview')
            .content.querySelector(
              `[data-section="${currentQuickView.dataset.section}"]`
            );

          HTMLUpdateUtility.viewTransition(
            currentQuickView,
            newQuickView,
            this.preProcessHtmlCallbacks,
            this.postProcessHtmlCallbacks
          );
        } else {
          document.querySelector('head title').innerHTML =
            html.querySelector('head title').innerHTML;

          HTMLUpdateUtility.viewTransition(
            document.querySelector('main'),
            html.querySelector('main'),
            this.preProcessHtmlCallbacks,
            this.postProcessHtmlCallbacks
          );
        }
      };
    }

    /**
     * Handle update product info.
     *
     * @param {Event} event
     * @returns {Void}
     */
    handleUpdateProductInfo(event) {
      return html => {
        this.updateLiveRegions(html);
        this.updateVariantInput();
        this.updateContactModal();
        this.updateMedia({
          name: event.target.dataset.name,
          value: event.target.value
        });

        if (!this.selectedVariant) {
          this.setUnavailable();
        }
      };
    }

    filterDropdownOptions() {
      const dropdowns = Array.from(this.querySelectorAll('select[data-name]'));
      if (!dropdowns.length) return;

      const optionNames = this.dataset.options.split('||');

      dropdowns.forEach(dropdown => {
        const thisOptionIndex = optionNames.indexOf(dropdown.dataset.name);
        if (thisOptionIndex === -1) return;

        const otherSelections = dropdowns
          .filter(d => d !== dropdown && d.value !== 'default')
          .map(d => ({ idx: optionNames.indexOf(d.dataset.name), value: d.value }))
          .filter(s => s.idx !== -1);

        const availableValues = new Set(
          this.variants
            .filter(variant => {
              if (!variant.available) return false;
              return otherSelections.every(
                ({ idx, value }) => variant[`option${idx + 1}`] === value
              );
            })
            .map(variant => variant[`option${thisOptionIndex + 1}`])
        );

        Array.from(dropdown.options).forEach(option => {
          if (option.value === 'default') return;
          option.hidden = !availableValues.has(option.value);
        });
      });
    }

    isEveryOptionSelected() {
      const optionNames = this.dataset.options.split('||');
      let isChosen = true;

      optionNames.forEach(optionName => {
        if (!isChosen) return;

        const elements = Array.from(this.querySelectorAll(`[data-name="${optionName}"]`));

        isChosen = elements.some(el => {
          if (el.type === 'radio') {
            return el.checked;
          } else {
            return el.value !== 'default';
          }
        });
      });

      return isChosen;
    }

    get form() {
      return document.querySelector(this.dataset.formId);
    }

    get productBar() {
      return document.querySelector('product-bar');
    }

    get submitButton() {
      return this.form.elements.add;
    }

    get quantity() {
      return this.form.elements.quantity;
    }

    get selectedOptionValues() {
      return Array.from(
        this.querySelectorAll('input[type="radio"]:checked, select'),
        el => ({
          name: el.dataset.name,
          value: el.value,
          id:
            el.dataset.optionValueId ||
            el.selectedOptions[0]?.dataset.optionValueId ||
            ''
        })
      );
    }

    get selectedVariant() {
      const selectedVariant = this.querySelector(
        '[data-selected-variant]'
      )?.innerHTML;
      return !!selectedVariant ? JSON.parse(selectedVariant) : null;
    }

    get isQuickviewSelector() {
      return this.closest('product-quickview');
    }
  }
  customElements.define('product-selector', ProductSelector);
}

if (!customElements.get('custom-property-checkbox')) {
  class CustomCheckbox extends HTMLElement {
    constructor() {
      super();

      this.mainCheckbox = this.querySelector(
        'input[type="checkbox"]'
      );
      this.input = this.querySelector('input[type="hidden"]');

      this.checkedValue = this.input.getAttribute('data-checked');
      this.uncheckedValue = this.input.getAttribute('data-unchecked');
    }

    connectedCallback() {
      this.mainCheckbox?.addEventListener(
        'change',
        this.handleMainChange.bind(this)
      );
    }

    handleMainChange(e) {
      const value = e.target.checked
        ? this.checkedValue
        : this.uncheckedValue;

      this.input.value = value;
    }
  }

  customElements.define('custom-property-checkbox', CustomCheckbox);
}
